import { useCallback, useMemo, useState } from 'react'
import {
  MessageContentFilter,
  SettingValue,
  OutboundPredefinedFilterAction,
  PredefinedFilter,
  OutboundPredefinedFilterPattern,
  PredefinedFilterMatch
} from 'types/Settings'
import { SettingsForm, useSettingsForm } from 'lib/settingsForm/useSettingsForm'
import { useStateField } from 'lib/settingsForm/useStateField'

export interface PredefinedFilterFormState {
  pattern: OutboundPredefinedFilterPattern
  action: OutboundPredefinedFilterAction
  matchSubject: SettingValue
  matchHeaders: SettingValue
  matchBody: SettingValue
  matchAttachments: SettingValue
}

export interface ValidatorDependencies {
  data: PredefinedFilter[]
}

export interface PredefinedFilterFormConfig {
  initialState: PredefinedFilterFormState
  delegateIsDirtyForm?: (isDirty: boolean) => void
}

export interface PredefinedFilterForm extends SettingsForm<PredefinedFilterFormState> {
  getStateAsFilter: () => Omit<MessageContentFilter, 'id'>
  errors: {
    pattern: string
    match: string
  }
}

export const usePredefinedFilterForm = ({
  initialState,
  delegateIsDirtyForm
}: PredefinedFilterFormConfig): PredefinedFilterForm => {
  const patternField = useStateField(initialState.pattern)
  const actionField = useStateField(initialState.action)
  const matchSubjectField = useStateField(initialState.matchSubject)
  const matchHeadersField = useStateField(initialState.matchHeaders)
  const matchBodyField = useStateField(initialState.matchBody)
  const matchAttachmentsField = useStateField(initialState.matchAttachments)
  const [patternError, setPatternError] = useState('')
  const [matchError, setMatchError] = useState('')

  const formConfig = useMemo(
    () => ({
      fields: {
        pattern: patternField,
        action: actionField,
        matchSubject: matchSubjectField,
        matchBody: matchBodyField,
        matchHeaders: matchHeadersField,
        matchAttachments: matchAttachmentsField
      },
      delegateIsDirtyForm
    }),
    [
      actionField,
      delegateIsDirtyForm,
      matchAttachmentsField,
      matchBodyField,
      matchHeadersField,
      matchSubjectField,
      patternField
    ]
  )
  const form = useSettingsForm<PredefinedFilterFormState>(formConfig)

  const validate = useCallback(
    (newPolicy: PredefinedFilter, dependencies: ValidatorDependencies) => {
      form.setError('')
      setPatternError('')
      setMatchError('')
      let nextPatternError = ''
      let nextMatchError = ''

      const findDuplicate = dependencies.data.find(entry => entry.pattern === newPolicy.pattern)
      if (findDuplicate) {
        nextPatternError = 'duplicate_value'
      }

      if (!newPolicy.pattern) {
        nextPatternError = 'missing_pattern'
      }

      const filteredMatch = Object.keys(newPolicy.match).reduce((perv, curr) => {
        const currentKey = curr as keyof PredefinedFilterMatch
        if (newPolicy.match[currentKey]) {
          // eslint-disable-next-line no-param-reassign
          perv[currentKey] = newPolicy.match[currentKey]
        }
        return perv
      }, {} as PredefinedFilterMatch)

      if (Object.keys(filteredMatch).length === 0) {
        nextMatchError = 'one_match_filter'
      }

      setMatchError(nextMatchError)
      setPatternError(nextPatternError)

      return !nextPatternError && !nextMatchError
    },
    [form]
  )

  const getMatch = useCallback(
    (): MessageContentFilter['match'] => ({
      ...(matchSubjectField.getValue() === SettingValue.ENABLED ? { subject: 1 } : {}),
      ...(matchBodyField.getValue() === SettingValue.ENABLED ? { body: 1 } : {}),
      ...(matchHeadersField.getValue() === SettingValue.ENABLED ? { headers: 1 } : {}),
      ...(matchAttachmentsField.getValue() === SettingValue.ENABLED ? { attachments: 1 } : {})
    }),
    [matchAttachmentsField, matchBodyField, matchHeadersField, matchSubjectField]
  )

  const getStateAsFilter = useCallback(
    (): Omit<MessageContentFilter, 'id'> => ({
      pattern: patternField.getValue(),
      action: actionField.getValue(),
      match: getMatch()
    }),
    [actionField, getMatch, patternField]
  )

  return useMemo(
    () => ({
      ...form,
      validate,
      getStateAsFilter,
      errors: {
        pattern: patternError,
        match: matchError
      }
    }),
    [form, getStateAsFilter, matchError, patternError, validate]
  )
}
